<template>
  <div class="ism-page-wrapper">

    <!-- Loading Skeleton -->
    <div v-if="pageLoading" class="ism-req-card">
      <div class="ism-req-header">
        <div class="ism-shimmer" style="width:72px;height:72px;border-radius:16px;flex-shrink:0;"></div>
        <div style="flex:1;display:flex;flex-direction:column;gap:10px;">
          <div class="ism-shimmer" style="width:200px;height:22px;border-radius:6px;"></div>
          <div class="ism-shimmer" style="width:120px;height:14px;border-radius:6px;"></div>
        </div>
      </div>
    </div>

    <!-- Main Card -->
    <div v-else class="ism-req-card">

      <!-- ── Header ── -->
      <div class="ism-req-header">

        <div class="ism-req-avatar-wrap">
          <PremiumAvatar
            :image="society.logo_url ? $resolveUploadUrl(society.logo_url) : null"
            :name="society.name"
            :size="72"
          />
        </div>

        <!-- Name + Badge -->
        <div class="ism-req-header-info">
          <h1 class="ism-req-name">{{ society.name }}</h1>
          <div class="ism-req-meta">
            <span class="ism-badge" :class="society.type == 1 ? 'ism-badge-blue' : 'ism-badge-accent'">
              {{ residentType(society.type) }}
            </span>
            <span class="ism-req-code" v-if="society.society_code">
              # {{ society.society_code }}
            </span>
          </div>
        </div>

        <!-- Actions -->
        <div class="ism-req-actions">
          <button class="ism-btn-approve" @click="confirmAccept" :disabled="loading">
            <v-icon size="16" color="#fff">mdi-check-circle-outline</v-icon>
            Approve
          </button>
          <button class="ism-btn-reject" @click="confirmReject" :disabled="loading">
            <v-icon size="16" color="#fff">mdi-close-circle-outline</v-icon>
            Reject
          </button>
          <button class="ism-btn-cancel" @click="$router.go(-1)">
            <v-icon size="16" color="var(--accent)">mdi-arrow-left</v-icon>
            Back
          </button>
        </div>
      </div>

      <!-- Divider -->
      <div class="ism-req-divider"></div>

      <!-- ── Stat Cards ── -->
      <div class="ism-req-stats">
        <div class="stat-card" style="--card-bar: linear-gradient(to right, #F26522, #ef4444); --icon-bg: rgba(242,101,34,0.1);">
          <div class="stat-card-top">
            <div class="stat-card-label">Houses</div>
            <div class="stat-card-icon">
              <v-icon color="#F26522" size="20">mdi-home-city-outline</v-icon>
            </div>
          </div>
          <div class="stat-card-value">{{ society.no_of_house ?? 0 }}</div>
          <div class="stat-card-delta">Total residential houses</div>
        </div>

        <div class="stat-card" style="--card-bar: linear-gradient(to right, #3b82f6, #2563eb); --icon-bg: rgba(59,130,246,0.1);">
          <div class="stat-card-top">
            <div class="stat-card-label">Shops</div>
            <div class="stat-card-icon">
              <v-icon color="#3b82f6" size="20">mdi-store-outline</v-icon>
            </div>
          </div>
          <div class="stat-card-value">{{ society.no_of_shop ?? 0 }}</div>
          <div class="stat-card-delta">Total commercial shops</div>
        </div>

        <div class="stat-card" style="--card-bar: linear-gradient(to right, #10b981, #059669); --icon-bg: rgba(16,185,129,0.1);">
          <div class="stat-card-top">
            <div class="stat-card-label">Type</div>
            <div class="stat-card-icon">
              <v-icon color="#10b981" size="20">mdi-domain</v-icon>
            </div>
          </div>
          <div class="stat-card-value" style="font-size:16px;">{{ residentType(society.type) }}</div>
          <div class="stat-card-delta">Society category</div>
        </div>

      </div>

      <!-- ── Info Grid ── -->
      <div class="ism-req-body">

        <!-- Society Info -->
        <div class="ism-req-section">
          <div class="ism-req-section-title">
            <v-icon size="16" color="var(--accent)" class="mr-2">mdi-office-building-outline</v-icon>
            Society Information
          </div>

          <div class="ism-req-info-grid">
            <div class="ism-req-info-item">
              <div class="ism-req-info-icon" style="background:rgba(242,101,34,0.1);">
                <v-icon size="18" color="#F26522">mdi-city-variant-outline</v-icon>
              </div>
              <div class="ism-req-info-content">
                <span class="ism-req-info-label">Society Name</span>
                <span class="ism-req-info-value">{{ society.name || '—' }}</span>
              </div>
            </div>

            <div class="ism-req-info-item">
              <div class="ism-req-info-icon" style="background:rgba(59,130,246,0.1);">
                <v-icon size="18" color="#3b82f6">mdi-domain</v-icon>
              </div>
              <div class="ism-req-info-content">
                <span class="ism-req-info-label">Resident Type</span>
                <span class="ism-req-info-value">{{ residentType(society.type) }}</span>
              </div>
            </div>

            <div class="ism-req-info-item ism-req-info-item--full">
              <div class="ism-req-info-icon" style="background:rgba(16,185,129,0.1);">
                <v-icon size="18" color="#10b981">mdi-map-marker-outline</v-icon>
              </div>
              <div class="ism-req-info-content">
                <span class="ism-req-info-label">Address</span>
                <span class="ism-req-info-value">
                  {{ society.address }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Vertical Divider -->
        <div class="ism-req-vdivider"></div>

        <!-- Contact Info -->
        <div class="ism-req-section">
          <div class="ism-req-section-title">
            <v-icon size="16" color="var(--accent)" class="mr-2">mdi-card-account-phone-outline</v-icon>
            Contact Information
          </div>

          <div class="ism-req-info-grid">
            <div class="ism-req-info-item ism-req-info-item--full">
              <div class="ism-req-info-icon" style="background:rgba(139,92,246,0.1);">
                <v-icon size="18" color="#8b5cf6">mdi-account-tie-outline</v-icon>
              </div>
              <div class="ism-req-info-content">
                <span class="ism-req-info-label">Contact Person</span>
                <span class="ism-req-info-value">{{ society.contact_person_name || '—' }}</span>
              </div>
            </div>

            <div class="ism-req-info-item ism-req-info-item--full">
              <div class="ism-req-info-icon" style="background:rgba(245,158,11,0.1);">
                <v-icon size="18" color="#f59e0b">mdi-phone-outline</v-icon>
              </div>
              <div class="ism-req-info-content">
                <span class="ism-req-info-label">Mobile Number</span>
                <span class="ism-req-info-value">{{ formatPhone(society.country_code, society.contact_person_number) }}</span>
              </div>
            </div>

            <div class="ism-req-info-item ism-req-info-item--full">
              <div class="ism-req-info-icon" style="background:rgba(239,68,68,0.1);">
                <v-icon size="18" color="#ef4444">mdi-email-outline</v-icon>
              </div>
              <div class="ism-req-info-content">
                <span class="ism-req-info-label">Email Address</span>
                <span class="ism-req-info-value" style="word-break:break-word;">{{ society.contact_person_email || '—' }}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Approve Confirm -->
    <ConfirmModal
      :isOpen="acceptDialog"
      type="success"
      title="Approve Society"
      :message="`Are you sure you want to approve &quot;${society.name}&quot;? This will activate the society on the platform.`"
      confirmText="Yes, Approve"
      cancelText="Cancel"
      @confirm="acceptSocietyRequest"
      @cancel="acceptDialog = false"
    />

    <!-- Reject Confirm -->
    <ConfirmModal
      :isOpen="rejectDialog"
      type="danger"
      title="Reject Society"
      :message="`Are you sure you want to reject &quot;${society.name}&quot;?`"
      confirmText="Yes, Reject"
      cancelText="Cancel"
      @confirm="rejectSocietyRequest"
      @cancel="rejectDialog = false"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import ConfirmModal from '../../common/ConfirmModal.vue';
import PremiumAvatar from '../../common/PremiumAvatar.vue';

export default {
  name: 'AcceptRejectRequest',
  components: { ConfirmModal, PremiumAvatar },

  data() {
    return {
      pageLoading:  true,
      loading:      false,
      society_id:   '',
      society:      { name: '' },
      acceptDialog: false,
      rejectDialog: false,
      residentTypes: [
        { id: 1, name: 'Residential' },
        { id: 2, name: 'Commercial' },
        { id: 3, name: 'Residential & Commercial' },
      ],
    };
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    residentType(type) {
      const found = this.residentTypes.find(t => t.id == type);
      return found ? found.name : 'N/A';
    },

    formatCurrency(c) {
      if (!c) return '—';
      const lower = c.toLowerCase();
      if (lower === 'rs' || lower === 'inr') return '₹ INR';
      return c.toUpperCase();
    },

    formatPhone(code, number) {
      if (!number) return '—';
      const prefix = code ? (code.startsWith('+') ? code : '+' + code) : '';
      return `${prefix} ${number}`.trim();
    },

    getSocietyDetails() {
      this.pageLoading = true;
      this.$axios.get(`superadmin/society/${this.society_id}`).then(res => {
        this.pageLoading = false;
        if (res.data.status) {
          this.society = res.data.society;
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
        }
      }).catch(() => { this.pageLoading = false; });
    },

    confirmAccept() { this.acceptDialog = true; },
    confirmReject() { this.rejectDialog = true; },

    acceptSocietyRequest() {
      this.acceptDialog = false;
      this.loading = true;
      this.$axios.post(`superadmin/society/approve/${this.society_id}`).then(res => {
        this.loading = false;
        if (res.data.status) {
          this.$router.push('/superadmin/society/requests');
          this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: res.data.message });
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
        }
      }).catch(() => { this.loading = false; });
    },

    rejectSocietyRequest() {
      this.rejectDialog = false;
      this.loading = true;
      this.$axios.post(`superadmin/society/reject/${this.society_id}`).then(res => {
        this.loading = false;
        if (res.data.status) {
          this.$router.push('/superadmin/society/requests');
          this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: res.data.message });
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
        }
      }).catch(() => { this.loading = false; });
    },
  },

  mounted() {
    this.society_id = this.$route.params.id;
    this.getSocietyDetails();
  },
};
</script>

<style scoped>
/* ── Page ─────────────────────────────────── */
.ism-page-wrapper { width: 100%; }

/* ── Main Card ─────────────────────────────── */
.ism-req-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  font-family: "Inter", sans-serif;
  overflow: hidden;
}

/* ── Header ───────────────────────────────── */
.ism-req-header {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 24px 28px;
  background: linear-gradient(180deg, var(--bg-primary) 0%, var(--card-bg) 100%);
  flex-wrap: wrap;
}

/* Avatar */
.ism-req-avatar-wrap { flex-shrink: 0; }
.ism-req-avatar-img {
  width: 72px; height: 72px;
  border-radius: 50%;
  object-fit: contain;
  border: 3px solid var(--card-bg);
  box-shadow: 0 4px 14px rgba(0,0,0,0.1);
  background: var(--bg-primary);
  display: block;
}
.ism-req-avatar-fallback {
  width: 72px; height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), var(--accent-hover));
  display: flex; align-items: center; justify-content: center;
  font-size: 30px; font-weight: 800; color: #fff;
  box-shadow: 0 6px 18px rgba(242,101,34,0.3);
  border: 3px solid var(--card-bg);
}

/* Name */
.ism-req-header-info { flex: 1; min-width: 0; }
.ism-req-name {
  font-size: 22px; font-weight: 800;
  color: var(--text-primary);
  letter-spacing: -0.02em; line-height: 1.2;
  margin: 0 0 8px 0;
}
.ism-req-meta {
  display: flex; align-items: center;
  gap: 8px; flex-wrap: wrap;
}
.ism-req-code {
  font-size: 11.5px; font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px; padding: 2px 8px;
  font-family: "JetBrains Mono", monospace;
}

/* Action Buttons */
.ism-req-actions {
  display: flex; gap: 10px;
  flex-shrink: 0; margin-left: auto;
  flex-wrap: wrap;
}

.ism-btn-approve {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px; border-radius: 10px; border: none;
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 13px; font-weight: 600; cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(16,185,129,0.25);
  white-space: nowrap;
}
.ism-btn-approve:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(16,185,129,0.35);
  filter: brightness(1.06);
}
.ism-btn-reject {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px; border-radius: 10px; border: none;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  font-family: "Inter", sans-serif;
  font-size: 13px; font-weight: 600; cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(239,68,68,0.25);
  white-space: nowrap;
}
.ism-btn-reject:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(239,68,68,0.35);
  filter: brightness(1.06);
}
.ism-btn-approve:disabled, .ism-btn-reject:disabled {
  opacity: 0.5; cursor: not-allowed; transform: none;
}

/* Divider */
.ism-req-divider { height: 1px; background: var(--border-color); }

/* ── Stat Cards Row ───────────────────────── */
.ism-req-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  padding: 24px 28px;
  border-bottom: 1px solid var(--border-color);
}

/* ── Body ─────────────────────────────────── */
.ism-req-body {
  display: flex;
  padding: 28px;
  gap: 28px;
}

.ism-req-section { flex: 1; min-width: 0; }

.ism-req-vdivider {
  width: 1px;
  background: linear-gradient(to bottom, transparent, var(--border-color2), transparent);
  flex-shrink: 0;
  opacity: 0.6;
}

.ism-req-section-title {
  font-size: 12.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--text-secondary);
  margin-bottom: 18px;
  display: flex; align-items: center;
  border-bottom: 2px solid var(--border-color);
  padding-bottom: 10px;
}

/* ── Info Grid ──────────────────────────────── */
.ism-req-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
}

.ism-req-info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  transition: all 0.2s;
}
.ism-req-info-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-sm);
  border-color: rgba(242,101,34,0.18);
  background: var(--card-bg);
}
.ism-req-info-item--full { grid-column: 1 / -1; }

.ism-req-info-icon {
  width: 36px; height: 36px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

.ism-req-info-content {
  display: flex; flex-direction: column; gap: 3px; min-width: 0;
}

.ism-req-info-label {
  font-size: 10.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--text-secondary);
}

.ism-req-info-value {
  font-size: 13.5px; font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
}

/* ── Responsive ───────────────────────────── */
@media (max-width: 900px) {
  .ism-req-body { flex-direction: column; padding: 20px; gap: 20px; }
  .ism-req-vdivider { width: 100%; height: 1px; background: linear-gradient(to right, transparent, var(--border-color2), transparent); }
  .ism-req-stats { padding: 20px; gap: 12px; }
}

@media (max-width: 600px) {
  .ism-req-header { padding: 18px; flex-direction: column; align-items: flex-start; }
  .ism-req-actions { margin-left: 0; width: 100%; }
  .ism-req-name { font-size: 18px; }
  .ism-btn-approve, .ism-btn-reject { padding: 9px 14px; font-size: 12.5px; flex: 1; justify-content: center; }
  .ism-btn-cancel { padding: 9px 14px; font-size: 12.5px; }
  .ism-req-stats { grid-template-columns: repeat(2, 1fr); }
  .ism-req-info-grid { grid-template-columns: 1fr; }
}
</style>
